import React, { useState, useRef } from "react";
import { Button } from "app/components/ui/button";

const testimonialData = [
  {
    id: 1,
    image: "/assets/smiling-van-driver-portrait.webp",
    altText: "Smiling Van Driver Portrait",
    name: "Michael R. - Uber driver",
    address: "Chicago, IL",
    testimonial: `"As a full-time Uber driver in Chicago, I was constantly dealing with network dead zones until I switched to VLE Mobile. The triple network coverage has been a game-changer - I haven't missed a single ride opportunity due to connection issues in months."`,
  },
  {
    id: 2,
    image: "/assets/smiling-van-drier-with-black-shirt.webp",
    altText: "Smiling Van Driver With Black Shirt",
    name: "John D. - Lyft driver",
    address: "Los Angeles, CA",
    testimonial: `"I've been using VLE Mobile for a few months now, and I've been really happy with the service. The coverage is great, and the customer service is excellent. I would definitely recommend VLE Mobile to anyone looking for a reliable and affordable mobile service."`,
  },
  {
    id: 3,
    image: "/assets/smiling-van-driver-with-spectacles.webp",
    altText: "Smiling Van Driver With Spectacles",
    name: "Dylan M. - Lyft driver",
    address: "New York, NY",
    testimonial: `"VLE Mobile has been a game-changer for my business. The triple network coverage has allowed me to stay connected and earn more rides without worrying about dropped calls or slow data speeds."`,
  },
];

export const PlatformDriversTestimonial = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalTestimonials = testimonialData.length;
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalTestimonials);
  };

  const goToPrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + totalTestimonials) % totalTestimonials
    );
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrev();
    }
  };

  const handleDotClick = (dotIndex: number) => {
    setCurrentIndex(dotIndex === totalTestimonials ? 0 : dotIndex);
  };

  const getVisibleDots = () => {
    const maxVisibleDots = Math.min(totalTestimonials, 4);

    if (totalTestimonials <= maxVisibleDots) {
      return Array.from({ length: totalTestimonials }, (_, i) => i);
    }

    const startIndex = Math.max(
      0,
      Math.min(currentIndex, totalTestimonials - maxVisibleDots + 1)
    );
    return Array.from(
      { length: Math.min(maxVisibleDots, totalTestimonials) },
      (_, i) => startIndex + i
    );
  };

  const visibleDots = getVisibleDots();

  const getCardWidth = (index: number) => {
    const positionDiff = index - currentIndex;
    if (positionDiff === 0) return "100%";
    if (positionDiff === 1) return "90%";
    if (positionDiff === 2) return "80%";
    return "0%";
  };

  const getCardOpacity = (index: number) => {
    const positionDiff = index - currentIndex;
    if (positionDiff >= 0 && positionDiff <= 2) return "opacity-100";
    return "opacity-0";
  };

  const getCardZIndex = (index: number) => {
    const positionDiff = index - currentIndex;
    return 3 - positionDiff;
  };

  const getCardTransform = (index: number) => {
    const positionDiff = index - currentIndex;

    if (positionDiff === 1)
      return "translate-y-[0.875rem] sm:translate-y-[1.25rem] md:translate-y-[1.625rem] lg:translate-y-[2rem] 2xl:translate-y-[2.5rem]";
    if (positionDiff === 2)
      return "translate-y-[1.75rem] sm:translate-y-[2.5rem] md:translate-y-[3.25rem] lg:translate-y-[4rem] 2xl:translate-y-[5rem]";
    if (positionDiff === -1)
      return "-translate-y-[0.875rem] sm:-translate-y-[1.25rem] md:-translate-y-[1.625rem] lg:-translate-y-[2rem] 2xl:-translate-y-[2.5rem]";

    return "translate-y-0";
  };

  return (
    <section className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12 py-9 sm:py-14 md:py-[5.5rem] lg:py-[7.5rem]">
      <div className="mb-8 text-center sm:mb-10 md:mb-12 lg:mb-14">
        <h2 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2.5vw+1rem),3.125rem)] font-normal leading-snug md:leading-normal lg:leading-[4rem]">
          What Platform Drivers Say About VLE Mobile
        </h2>
        <p className="text-[#1D1D1B] text-[0.925rem] sm:text-base md:text-lg lg:text-xl font-normal leading-5 sm:leading-6 md:leading-7 lg:leading-[2.1rem]">
          Drivers Testimonials
        </p>
      </div>
      <div className="">
        <div
          ref={containerRef}
          className="relative flex items-center justify-center gap-3 sm:gap-5 md:gap-7 lg:gap-9 xl:gap-11 2xl:gap-[3.25rem] h-[16rem] sm:h-[20rem] md:h-[24rem] lg:h-[32rem] 2xl:h-[36rem] w-full max-w-[66.5rem] mx-auto sm:select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Left Navigation Arrow */}
          <Button
            type="button"
            variant="ghost"
            className="hidden justify-center items-center rounded-3xl border border-black transition-all duration-200 sm:flex shrink-0 hover:bg-gray-100 size-8 sm:size-10 lg:size-12"
            onClick={goToPrev}
            disabled={currentIndex === 0}
            aria-label="Previous testimonial"
          >
            <img
              src="/assets/right-arrow.svg"
              alt="Right Arrow"
              className="w-4 sm:w-[1.125rem] lg:w-[1.1875rem] h-3.5 sm:h-4 lg:h-[1.0625rem] rotate-180"
            />
          </Button>
          {/* Testimonial Cards */}
          <ul className="relative w-full h-full max-h-[30.75rem]">
            {testimonialData.map((testimonial, index) => {
              const shouldRender =
                index >= currentIndex && index <= currentIndex + 2;

              return shouldRender ? (
                <li
                  key={testimonial.id}
                  className={`absolute top-0 left-1/2 -translate-x-1/2 transition-all duration-500 ease-in-out ${getCardOpacity(
                    index
                  )} ${getCardTransform(index)}`}
                  style={{
                    width: getCardWidth(index),
                    zIndex: getCardZIndex(index),
                  }}
                >
                  <div className="flex items-end w-full h-full max-h-[13rem] sm:max-h-[16rem] md:max-h-[19rem] lg:max-h-[24rem] 2xl:max-h-[28.125rem] bg-white rounded-[0.625rem] shadow-[0px_20px_60px_0px_rgba(46,33,61,0.08)] mx-auto">
                    <div className="flex items-end px-4 sm:px-6 h-full shrink-0 md:px-8 lg:px-10 2xl:px-12">
                      <div className="w-full max-w-[8.5rem] sm:max-w-40 md:max-w-52 lg:max-w-64 2xl:max-w-[19.3125rem] aspect-[0.609/1] md:aspect-[0.628/1] shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.altText}
                          className={`object-cover size-full rounded-t-[0.625rem] transition-opacity duration-500 ease-in-out ${
                            index === currentIndex ? "opacity-100" : "opacity-0"
                          }`}
                          loading={index === currentIndex ? "eager" : "lazy"}
                        />
                      </div>
                    </div>
                    <div
                      className={`py-5 sm:py-8 md:py-10 lg:py-12 2xl:py-[3.75rem] pr-5 sm:pr-8 md:pr-10 lg:pr-12 2xl:pr-[3.95rem] transition-opacity duration-500 ease-in-out ${
                        index === currentIndex ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      <div className="mb-2 sm:mb-3 md:mb-4 lg:mb-5">
                        <h3 className="capitalize font-mobile text-black text-lg sm:text-xl md:text-2xl lg:text-[1.75rem] 2xl:text-3xl font-normal leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7 2xl:leading-[1.875rem] line-clamp-1">
                          {testimonial.name}
                        </h3>
                        <span className="inline-block capitalize text-black text-sm sm:text-[0.925rem] md:text-base lg:text-lg 2xl:text-xl font-normal leading-[1.1875rem] sm:leading-5 md:leading-6 lg:leading-7 2xl:leading-[1.875rem] line-clamp-1">
                          {testimonial.address}
                        </span>
                      </div>
                      <p className="text-black text-xs sm:text-sm md:text-[0.9375rem] lg:text-[1.0625rem] font-normal leading-5 sm:leading-6 md:leading-7 lg:leading-8 line-clamp-6">
                        {testimonial.testimonial}
                      </p>
                    </div>
                  </div>
                </li>
              ) : null;
            })}
          </ul>
          {/* Right Navigation Arrow */}
          <Button
            type="button"
            variant="ghost"
            className="hidden justify-center items-center rounded-3xl border border-black transition-all duration-200 sm:flex shrink-0 hover:bg-gray-100 size-8 sm:size-10 lg:size-12"
            onClick={goToNext}
            disabled={currentIndex === totalTestimonials - 1}
            aria-label="Next testimonial"
          >
            <img
              src="/assets/right-arrow.svg"
              alt="Right Arrow"
              className="w-4 sm:w-[1.125rem] lg:w-[1.1875rem] h-3.5 sm:h-4 lg:h-[1.0625rem]"
            />
          </Button>
        </div>
        {totalTestimonials > 1 && (
          <div className="flex justify-center items-center sm:hidden h-3 mb-[1.625rem] relative">
            <div className="flex gap-2 items-center">
              {[0, 1, 2, 3].map((dotIndex) => (
                <React.Fragment key={dotIndex}>
                  <Button
                    type="button"
                    variant="ghost"
                    className={`z-[1] transition-all duration-300 rounded-full ${
                      dotIndex === currentIndex
                        ? "bg-[linear-gradient(90deg,#FC5F47_0%,#FC5F470A_100%)] hover:bg-[#FC5036] size-[0.28125rem]"
                        : dotIndex === currentIndex + 1
                        ? "bg-[linear-gradient(90deg,#FC5F470A_0%,#FC5F47_100%)] hover:bg-[#FC5036] size-3"
                        : "bg-black/20 hover:bg-black/25 size-2"
                    }`}
                    onClick={() => handleDotClick(dotIndex)}
                  />
                  {dotIndex === currentIndex && (
                    <div className="relative -ml-[1.4rem] -mr-[1.8125rem] w-12 h-3">
                      <svg
                        viewBox="0 0 59 12"
                        className="absolute top-0 left-0 w-full h-full"
                        preserveAspectRatio="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M14.6402 3.61187C14.5612 3.60402 14.4811 3.6 14.4 3.6C13.0745 3.6 12 4.67452 12 6C12 7.32548 13.0745 8.4 14.4 8.4C14.4811 8.4 14.5612 8.39598 14.6402 8.38812L14.6402 8.4C15.4094 8.4 16.4789 8.12703 17.8114 7.78693C20.0127 7.22506 22.9317 6.48 26.4002 6.48C29.3051 6.48 31.9225 7.9824 34.4263 9.41959C35.9921 10.3183 37.5134 11.1916 39.0328 11.656C39.6605 11.8787 40.3362 12 41.0402 12C41.0604 12 41.0806 11.9999 41.1007 11.9997C41.1206 11.9999 41.1404 12 41.1602 12L41.1602 11.9988C44.4185 11.9349 47.0402 9.27362 47.0402 6C47.0402 2.72638 44.4185 0.0650907 41.1602 0.00118033L41.1602 3.49374e-06C41.1408 3.49459e-06 41.1213 0.000107446 41.1018 0.000314394C41.0813 0.000108401 41.0608 4.45176e-06 41.0402 4.45266e-06C40.1361 4.49218e-06 39.2786 0.199993 38.5097 0.558148C37.3433 1.00994 36.1617 1.68482 34.9301 2.38831C32.3995 3.83376 29.6574 5.4 26.4002 5.4C22.6146 5.4 19.8069 4.69478 17.7063 4.16717C16.451 3.85187 15.4482 3.6 14.6402 3.6L14.6402 3.61187Z"
                          fill="url(#paint0_linear_139_6355)"
                        />
                        <defs>
                          <linearGradient
                            id="paint0_linear_139_6355"
                            x1="29.5202"
                            y1="11.9995"
                            x2="29.5202"
                            y2="0.000237577"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#FC5F47" />
                            <stop offset="1" stopColor="#FFA699" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
